import {NgClass, NgTemplateOutlet, UpperCasePipe} from '@angular/common';
import {Component, CUSTOM_ELEMENTS_SCHEMA, effect, input, output, signal, viewChild} from '@angular/core';
import {FullCalendarComponent, FullCalendarModule} from '@fullcalendar/angular';
import {CalendarOptions, DatesSetArg} from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import {MeetingDto} from '../../../dto/meeting-dto';
import {Meeting} from '../../../service/meeting';

@Component({
  selector: 'app-planner-calendar',
  imports: [
    FullCalendarModule,
    NgClass,
    NgTemplateOutlet,
    UpperCasePipe,
  ],
  templateUrl: './planner-calendar.html',
  styleUrl: './planner-calendar.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class PlannerCalendar {
  readonly currentUserId = input<string | undefined>(undefined);
  readonly meetingsInput = input<MeetingDto[] | undefined>(undefined);
  readonly selectedDate = input<Date | undefined>(undefined);
  readonly canEdit = input<boolean>(false);
  readonly showCreateAction = input<boolean>(false);

  readonly monthChanged = output<DatesSetArg>();
  readonly daySelected = output<{ date: Date; openPanel: boolean }>();
  readonly eventSelected = output<string>();
  readonly createClicked = output<void>();

  protected readonly calendar = viewChild.required(FullCalendarComponent);
  private lastRange: { start: Date; end: Date } | undefined;
  private currentMonthIndex = new Date().getMonth();
  private readonly weekdayFormatter = new Intl.DateTimeFormat('nl-NL', {weekday: 'short'});
  private readonly meetings = signal<MeetingDto[]>([]);

  protected readonly chosenDate = signal<Date>(new Date());
  protected readonly currentMonthLabel = signal<string>('');
  protected readonly weekdayHeaders = signal<string[]>(['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo']);
  protected readonly visibleDays = signal<{
    date: Date;
    iso: string;
    isToday: boolean;
    isOtherMonth: boolean;
    events: {
      id: string;
      title: string;
      timeText: string;
      timeRange: string;
      dateLong: string;
    }[];
  }[]>([]);
  protected readonly selectedDayEvents = signal<{
    id: string;
    title: string;
    timeText: string;
    timeRange: string;
    dateLong: string;
  }[]>([]);
  protected readonly calendarOptions: CalendarOptions = {
    initialView: 'dayGridMonth',
    firstDay: 1,
    height: 'auto',
    plugins: [dayGridPlugin, interactionPlugin],
    dateClick: (ev) => this.selectDay(ev.date, this.canEdit()),
    datesSet: this.handleDatesSet.bind(this),
    locale: 'nl',
    showNonCurrentDates: true,
  };

  constructor(private readonly meetingService: Meeting) {
    effect(() => {
      this.currentUserId();
      const meetings = this.meetingsInput();
      const selectedDate = this.selectedDate();

      if (selectedDate) {
        this.chosenDate.set(new Date(selectedDate));
      }

      if (meetings !== undefined && this.lastRange) {
        this.meetings.set(meetings);
        this.mapMeetingsToEvents(meetings);
        return;
      }
    });
  }

  protected selectDay(date: Date, openPanel: boolean = false) {
    this.chosenDate.set(date);
    this.syncSelectedDayEvents();
    this.daySelected.emit({date, openPanel});
  }

  protected selectEvent(eventId: string) {
    const event = this.visibleDays()
      .flatMap((day) => day.events)
      .find((dayEvent) => dayEvent.id === eventId);

    if (!event) {
      return;
    }

    const meeting = this.meetings().find((currentMeeting) => currentMeeting.id === eventId);
    if (meeting?.startDate) {
      this.selectDay(new Date(meeting.startDate));
    }
    this.eventSelected.emit(eventId);
  }

  private handleDatesSet(ev: DatesSetArg) {
    const currentDate = ev.view.currentStart;
    this.lastRange = {start: new Date(ev.start), end: new Date(ev.end)};
    this.currentMonthIndex = currentDate.getMonth();
    this.currentMonthLabel.set(ev.view.title);
    this.monthChanged.emit(ev);

    const meetings = this.meetingsInput();
    if (meetings !== undefined) {
      this.meetings.set(meetings);
      this.mapMeetingsToEvents(meetings);
      return;
    }

    this.loadMeetingsForMonth(currentDate);
  }

  private loadMeetingsForMonth(currentDate: Date) {
    this.meetingService
      .retrieveMeetingsByMonth(
        currentDate.getMonth() + 1,
        currentDate.getFullYear(),
        ['id', 'name', 'startDate', 'endDate', 'userIds'],
      )
      .subscribe({
        next: (response) => {
          const userId = this.currentUserId();
          const filteredMeetings = userId
            ? response.filter((meeting) => meeting.userIds?.includes(userId))
            : response;
          this.meetings.set(filteredMeetings);
          this.mapMeetingsToEvents(filteredMeetings);
        },
        error: () => {
          this.meetings.set([]);
          this.mapMeetingsToEvents([]);
        },
      });
  }

  private mapMeetingsToEvents(meetings: MeetingDto[]) {
    const api = this.calendar().getApi();
    api.removeAllEvents();

    meetings.forEach((meeting) => {
      api.addEvent({
        id: meeting.id ?? `${meeting.name}-${meeting.startDate}`,
        title: meeting.name,
        start: meeting.startDate,
        end: meeting.endDate,
        allDay: false,
      });
    });
    this.rebuildVisibleDays();
  }

  private rebuildVisibleDays() {
    if (!this.lastRange) {
      return;
    }

    const api = this.calendar().getApi();
    const events = api.getEvents();
    const days: {
      date: Date;
      iso: string;
      isToday: boolean;
      isOtherMonth: boolean;
      events: {
        id: string;
        title: string;
        timeText: string;
        timeRange: string;
        dateLong: string;
      }[];
    }[] = [];
    const cursor = new Date(this.lastRange.start);
    const end = new Date(this.lastRange.end);

    while (cursor < end) {
      const date = new Date(cursor);
      const iso = date.toISOString().split('T')[0];
      const dayEvents = events
        .filter((ev) => this.isSameDay(ev.start ?? ev.startStr, date))
        .map((ev) => ({
          id: ev.id,
          title: ev.title,
          timeText: this.formatTime(ev.start ?? ev.startStr),
          timeRange: this.formatTimeRange(
            ev.start ?? ev.startStr,
            ev.end ?? ev.endStr,
          ),
          dateLong: this.formatDateLong(ev.start ?? ev.startStr),
        }));

      days.push({
        date,
        iso,
        isToday: this.isSameDay(new Date(), date),
        isOtherMonth: date.getMonth() !== this.currentMonthIndex,
        events: dayEvents,
      });

      cursor.setDate(cursor.getDate() + 1);
    }

    this.visibleDays.set(days);
    this.weekdayHeaders.set(
      days.slice(0, 7).map((day) => this.weekdayFormatter.format(day.date)),
    );
    this.syncSelectedDayEvents();
  }

  private syncSelectedDayEvents() {
    const selectedIso = this.chosenDate().toISOString().split('T')[0];
    const match = this.visibleDays().find((day) => day.iso === selectedIso);
    this.selectedDayEvents.set(match?.events ?? []);
  }

  private isSameDay(a: Date | string | null, b: Date) {
    if (!a) {
      return false;
    }
    const date = typeof a === 'string' ? new Date(a) : a;

    return (
      date.getFullYear() === b.getFullYear() &&
      date.getMonth() === b.getMonth() &&
      date.getDate() === b.getDate()
    );
  }

  private formatTime(dateLike: Date | string | null) {
    if (!dateLike) {
      return '';
    }
    const date = typeof dateLike === 'string' ? new Date(dateLike) : dateLike;
    return date.toLocaleTimeString('nl-NL', {
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  private formatTimeRange(
    start: Date | string | null,
    end: Date | string | null,
  ) {
    const startText = this.formatTime(start);
    const endText = this.formatTime(end);
    return endText ? `${startText} - ${endText}` : startText;
  }

  private formatDateLong(dateLike: Date | string | null) {
    if (!dateLike) {
      return '';
    }
    const date = typeof dateLike === 'string' ? new Date(dateLike) : dateLike;
    return date.toLocaleDateString('nl-NL', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  }
}
